import { useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import FieldLabel from 'components/shared/form/FieldLabel';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import StandardModal from 'components/shared/StandardModal';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { WorkflowLookup } from 'models/WorkflowLookup';
import { adminClientsQueryKeys, adminIndexClients } from 'services/api/admin/clients';
import { createAdminWorkflowLookup } from 'services/api/admin/workflowLookups';
import { BodySmall } from 'styles/typography';

type ClientOption = { id: string; name: string };

type Props = {
  lookup: WorkflowLookup;
  show: boolean;
  onClose: () => void;
  onCreated: (created: WorkflowLookup) => void;
};

// Gives one client its own copy of a global list. The copy starts from the Olio default's current
// entries rather than empty, so a client that wants nine of twelve options removes three instead of
// retyping nine — and there is never a half-built list live in the product.
export default function CustomizeForClientModal({ lookup, show, onClose, onCreated }: Props) {
  const [client, setClient] = useState<ClientOption | null>(null);

  const clientOptions = useInfiniteOptions<ClientOption>({
    queryKey: adminClientsQueryKeys.index({ scope: 'workflowLookups' }),
    fetchFn: (params, signal) => adminIndexClients(params, signal),
    params: { sortBy: 'name asc' },
  });

  const { mutate, isPending } = useMutateWithToast({
    mutationFn: ({ clientId }: { clientId: string }) => createAdminWorkflowLookup({ key: lookup.key, clientId }),
  });

  if (!show) return null;

  const handleCreate = () => {
    if (!client) return;

    mutate(
      { clientId: client.id },
      {
        onSuccess: (created: WorkflowLookup) => {
          onClose();
          onCreated(created);
        },
      }
    );
  };

  return (
    <StandardModal title={`Customize ${lookup.label}`} onCancel={onClose}>
      <Description>
        This client gets its own copy of the list, starting from the {lookup.activeItemCount} options in the Olio
        default. Editing it affects only them, and every other client keeps the default.
      </Description>

      <FieldContainer>
        <FieldLabel>Client</FieldLabel>
        <Select<ClientOption>
          {...clientOptions}
          value={client}
          onChange={setClient}
          getOptionValue={(option) => option.id}
          getOptionLabel={(option) => option.name}
        />
      </FieldContainer>

      <StandardModal.Actions>
        <Button variant='ghost' onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button variant='primary' onClick={handleCreate} loading={isPending} disabled={!client || isPending}>
          Customize
        </Button>
      </StandardModal.Actions>
    </StandardModal>
  );
}

const Description = styled(BodySmall)`
  display: block;
  margin-bottom: 16px;
`;

const FieldContainer = styled.div`
  margin-bottom: 16px;
`;
